import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ChatMessage, ConversationState } from "@/components/agent/chat/types";
import { saveSystemMessage } from "@/services/messages";
import { Json } from "@/types/database.types";

/**
 * Interface representing the actual database message structure
 * This differs from the TypeScript type definitions but matches the real DB schema
 */
interface DatabaseMessage {
  id: string;
  content: string;
  conversation_id: string;
  created_at: string;
  sender_type: "user" | "ai" | "agent_chat_user";
  sender_id?: string | null;
  openai_message_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

/**
 * Hook to handle the reopening of a closed conversation
 *
 * Note: The database has a mismatch between TypeScript types and actual schema:
 * - TypeScript expects 'role', but DB uses 'sender_type'
 * - DB uses sender_type values: "ai", "user", "agent_chat_user"
 * - UI uses type values: "agent", "user", "system"
 */
export const useConversationReopening = (
  conversationId: string | null,
  setChatMessages: (messages: ChatMessage[]) => void,
  setConversationState: (state: ConversationState) => void,
  onError?: (message: string) => void
) => {
  const { toast } = useToast();
  const [isReopening, setIsReopening] = useState(false);

  const handleReopenConversation = async () => {
    if (!conversationId) {
      if (onError) {
        onError("Cannot resume conversation - no conversation ID found.");
      } else {
        toast({
          variant: "destructive",
          title: "Cannot resume conversation",
          description: "No conversation ID found. Please start a new conversation.",
        });
      }
      return;
    }

    if (isReopening) return;

    setIsReopening(true);

    try {
      // Update conversation state back to 'open' in the database
      const { error: updateError } = await supabase
        .from("conversations")
        .update({ state: "open" })
        .eq("id", conversationId);

      if (updateError) throw updateError;

      // Add a system message indicating the conversation was reopened
      const systemMessage: ChatMessage = {
        type: "system",
        content: "Conversation reopened",
        timestamp: new Date().toISOString(),
      };

      // Get current messages with proper type casting to handle DB schema mismatch
      const { data: messagesData, error: messagesError } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

      if (messagesError) throw messagesError;

      // Transform to ChatMessage format if data exists
      const currentMessages: ChatMessage[] = [];

      if (messagesData && messagesData.length > 0) {
        // Type assertion to match the actual database schema
        const dbMessages = messagesData as unknown as DatabaseMessage[];

        for (const message of dbMessages) {
          // Check for system messages (ai messages with is_system_message flag)
          if (
            message.metadata &&
            typeof message.metadata === "object" &&
            "is_system_message" in message.metadata &&
            message.metadata.is_system_message
          ) {
            currentMessages.push({
              type: "system",
              content: message.content,
              timestamp: message.created_at,
            });
          }
          // For normal user/agent messages
          else if (message.sender_type === "user") {
            currentMessages.push({
              type: "user",
              content: message.content,
              timestamp: message.created_at,
            });
          }
          // AI messages are displayed as agent messages
          else {
            currentMessages.push({
              type: "agent",
              content: message.content,
              timestamp: message.created_at,
            });
          }
        }
      }

      // Add the system message to the current messages and update the UI
      const updatedMessages = [...currentMessages, systemMessage];
      setChatMessages(updatedMessages);

      // Also save the system message to the database
      await saveSystemMessage(conversationId, systemMessage.content);

      // Set conversation state to open
      setConversationState("open");

      toast({
        title: "Conversation resumed",
        description: "You can now continue your conversation.",
      });
    } catch (error) {
      if (onError) {
        onError("Failed to reopen conversation. Please try again.");
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to reopen conversation. Please try again.",
        });
      }
    } finally {
      setIsReopening(false);
    }
  };

  return { handleReopenConversation, isReopening };
};
